import { useOrientation } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";
import { filter, Subject, switchMap } from "rxjs";

/**
 * Detects when screen orientation changes while keyboard is open.
 * Only emits true when BOTH conditions are met:
 * 1. Screen orientation changes (landscape ↔ portrait)
 * 2. Keyboard is currently open
 *
 * Does NOT emit when:
 * - Keyboard opens/closes without rotation change
 * - Orientation changes while keyboard is closed
 *
 * @returns boolean indicating if rotation occurred with keyboard open
 */
export function useScreenRotatedWhileKeyboardIsOpen(
  callback: () => void
) {
  const keyboardOpen = useIsOnScreenKeyboardOpen();
  const orientation = useOrientation().type;

    const keyboardOpenedChanged = useRef(new Subject<boolean>());
    const orientationChanged = useRef(new Subject<string>());

    const callbackRef = useRef(callback);

    useEffect(() => callbackRef.current = callback, [callback]);
    useEffect(() => keyboardOpenedChanged.current.next(keyboardOpen), [keyboardOpen]);
    useEffect(() => orientationChanged.current.next(orientation), [orientation]);

    useEffect(() => {
        const subscription = keyboardOpenedChanged.current.pipe(
            filter(isKeyboardOpen => isKeyboardOpen === true),
            switchMap((isKeyboardOpen) => orientationChanged.current)
        ).subscribe(() => callbackRef.current());

        return () => {
            subscription.unsubscribe();
        };
    }, []);
}

const isKeyboardInput = (elem: HTMLElement) =>
  (
    (elem.tagName === 'INPUT' &&
      !['button', 'submit', 'checkbox', 'file', 'image'].includes(
        (elem as HTMLInputElement).type,
      )) ||
    elem.tagName === 'TEXTAREA' ||
    elem.tagName === 'SELECT' ||
    elem.isContentEditable
  );

const useIsOnScreenKeyboardOpen = () => {
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && isKeyboardInput(e.target)) {
        setOpen(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && isKeyboardInput(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return isOpen;
};
